"use client";

import { useEffect, useRef } from "react";
import {
  useHandsOnControls,
  useHandsOnPiano,
} from "@/hooks/journey/use-hands-on-controls";
import { HandsOnHint } from "./hands-on-hint";
import { TRACE_EDGE } from "./panel-metrics";

/**
 * The controls a practice run needs whatever it is practising: how far along,
 * how to start over, and what happens when it is done.
 *
 * Shared by the single-hand runs and the both-hands run. Identical in
 * intention on all three — only the accent differs, and it is a parameter.
 */

/** Passes drawn as dots before the counter switches to a number. */
export const PASS_DOTS = 4;

/**
 * How tall an act of the end-of-run card is.
 *
 * The posture of reference is a tablet on the music stand, answered with a
 * finger that has just come off a key — so 48 px is the floor and 52 is what
 * these two get, boxes included. « Continuer » used to be a bare 12 px label
 * in a 24 px box: the one control that moves the parcours forward was the
 * smallest target on the screen, and it looked exactly like the offer sitting
 * next to it.
 */
export const ACT_H_PX = 52;

/** Same, for the offer under the rule: reachable, not competing. */
export const ASIDE_H_PX = 44;

export function Progress({
  percent,
  markers,
  total,
  color,
  compact = false,
}: {
  percent: number;
  markers: ReadonlySet<number>;
  total: number;
  color: string;
  /** In a row rather than stacked: the bar carries no margin of its own. */
  compact?: boolean;
}) {
  return (
    <div style={{ marginBottom: compact ? 0 : 14 }}>
      <div
        style={{
          position: "relative",
          height: 5,
          width: "100%",
          borderRadius: 999,
          background: "rgba(255, 255, 255, 0.06)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${percent}%`,
            background: color,
            boxShadow: `0 0 8px ${color}`,
            borderRadius: 999,
            transition: "width 0.2s ease-out",
          }}
        />

        {total > 0 &&
          [...markers].map((index) => (
            <span
              key={index}
              aria-hidden
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${(index / total) * 100}%`,
                width: 2,
                // `--color-bg-base` does not exist in `globals.css` — the
                // move markers of every single-hand run have been drawing
                // themselves in `transparent` since the day they were added.
                background: "var(--color-bg-deep)",
                opacity: 0.9,
              }}
            />
          ))}
      </div>
    </div>
  );
}

/**
 * Always available, and never gated on having made a mistake.
 *
 * It used to appear only past the first wrong note, which left the one
 * player who most needed it — stuck without having played anything wrong —
 * with no way out of the run but the stepper.
 */
export function RetrySecondaryButton({
  onRetry,
  compact = false,
}: {
  onRetry: () => void;
  /** In the band across the roll: a control in a row, not a block. */
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onRetry}
      className={`transition-colors hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] ${
        compact ? "shrink-0 whitespace-nowrap" : "w-full"
      }`}
      style={{
        padding: compact ? "5px 10px" : "10px 12px",
        borderRadius: 8,
        background: "transparent",
        border: "1px solid var(--color-border-subtle)",
        color: "var(--color-text-secondary)",
        fontSize: 11,
        cursor: "pointer",
      }}
    >
      ↺ Reprendre depuis le début
    </button>
  );
}

/**
 * End of a clean run. Playing it again is the primary action, moving on the
 * secondary — the whole point of a short unit is that it can be repeated
 * four times in two or three minutes, and an affordance that buries the
 * replay hands back units played once each. Past `PASS_DOTS` the two trade
 * places: the counter is full, and a filled button still saying "encore" over
 * a full counter is arguing with its own counter.
 *
 * The passes are counted and drawn, which is what makes "encore une fois" an
 * act of progress rather than an undo. What is *not* shown is the median
 * inter-group interval that used to sit here: a speed figure, with no
 * reference value, published by a step that deliberately does not grade
 * speed — and read by the only party who can do nothing with it.
 *
 * ## Answering it without a hand
 *
 * This is the screen the player meets four times per run, and every one of
 * those meetings used to cost eight fingers coming off the keys. Both acts
 * are on the instrument here: playing is another pass, the pedal moves on
 * (`useHandsOnControls`). Doing nothing does nothing: moving on is always
 * the player's act, never a timer's.
 *
 * ## It is not a card
 *
 * It was one: a tinted box with its own border, inside a 560 px panel that
 * already has a border, costing 24 px of width to say the same thing twice.
 * Inside it everything was written at the rank of the chrome around it —
 * headline 12 px, the header 11, the footer 11 — so the one thing the screen
 * is about did not outrank the frame it sits in, and the panel read as having
 * no subject. What replaces it is an outline: the count as context, the
 * sentence as the subject at 21 px, two acts of equal height, and the offer
 * to listen below a rule, because it is an offer and not a step.
 *
 * The hand keeps its colour. It moves onto the things that are *about* the
 * hand — the pass dots and the filled act — instead of washing a box.
 */
export function CompletedFeedback({
  passes,
  onRetry,
  onContinue,
  handColor,
  primaryColor = handColor,
  audition,
  title,
  note,
  retryLabel = "Encore une fois",
  continueLabel = "Continuer →",
  ready,
  divided = false,
}: {
  passes: number;
  onRetry: () => void;
  onContinue: () => void;
  handColor: string;
  /**
   * Fill of the primary button, when the card's own accent would make it read
   * as secondary. The runs in time paint their chrome achromatically —
   * anything tinted there claims a hand — and a grey button next to a grey
   * link stops looking like the thing to press.
   */
  readonly primaryColor?: string;
  /** Rendered next to "Continuer": hearing the fragment is informative here. */
  readonly audition?: React.ReactNode;
  /**
   * Overrides the headline. A run in time has gone somewhere else than a run
   * without one, and says so in its own words.
   */
  readonly title?: string;
  /**
   * One extra line under the headline, when there is something certain to
   * add. Passing `null` says there is nothing, and suppresses the default.
   */
  readonly note?: string | null;
  readonly retryLabel?: string;
  /**
   * Overrides « Continuer → ».
   *
   * The word is the same on nine screens of the parcours, and on the free
   * régime of the two-hand step it means something none of the others do:
   * take the metronome. A control that changes the régime has to say so.
   */
  readonly continueLabel?: string;
  /**
   * Whether the two acts have traded places, overriding the pass count.
   *
   * `PASS_DOTS` is a dosage — how many repetitions a short unit is worth —
   * and on the free régime the step has something better to go on: whether
   * the passes are coming out evenly. See `readyForTime` in `mastery.ts`.
   */
  readonly ready?: boolean;
  /**
   * Whether the verdict opens on a rule.
   *
   * `true` where the card follows the run map: the map is the evidence and
   * this is what is said about it, and with no box left around the verdict
   * the two would run into one another. The practice step passes nothing —
   * there the card *is* the body of the panel, and a rule two rows under the
   * header would be the panel drawing its own separator twice.
   */
  readonly divided?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const handsOn = useHandsOnPiano();

  // The card is the one screen of the parcours the player meets four times per
  // run, so it is the one that has to be answerable with the hands where they
  // are. Playing is another pass — whatever they strike, the hands are already
  // on the phrase — and the pedal moves on.
  useHandsOnControls({
    enabled: true,
    onAdvance: onContinue,
    onPlay: onRetry,
  });

  // Past the dosage the card was drawn for, the two acts trade places. The
  // dots are full: repeating is still there and still free, but the sentence
  // the screen is making has changed, and a filled button that says "encore"
  // over a full counter is arguing with its own counter.
  const done = ready ?? passes >= PASS_DOTS;

  // The retry control unmounts when the run completes, so a player who was on
  // it is dropped onto `<body>` — at the one moment of the run that must not
  // be missed, and after a run that can be twenty groups long. So focus still
  // moves; what it no longer lands on is the card.
  //
  // The card was the wrong target twice over. It is not a control, and the
  // ring it drew showed for everyone rather than for keyboard users only:
  // on this screen the player has very likely just played the passage **on
  // the computer keyboard**, so the browser holds the last input as a key and
  // a programmatic focus matches `focus-visible`. A whole outlined box then
  // appears for someone who never touched Tab. The primary button is a
  // control, it is the act the screen is offering, and its own ring is the
  // size of a button.
  useEffect(() => {
    ref.current?.focus();
  }, []);

  // Why play it again, on the screen that offers to. Motor consolidation
  // wants the repetition, and « encore une fois » on its own reads as an
  // undo — as if something had gone wrong. Once the dots are full the
  // sentence would be arguing with its own counter, so it goes.
  const line =
    note === undefined
      ? done
        ? null
        : "Recommence pour le mémoriser."
      : note;

  // The word « Continuer » without the arrow, which is a direction and not
  // part of the verb. It is what the pedal answers for.
  const continueWord = continueLabel.replace(" →", "");

  // What the pedal does, in the words of the button it answers for. « Pédale ·
  // continuer » was true on the eight screens where continuing is the next
  // step and false on the one where it takes the metronome.
  const continueHint = continueWord.toLocaleLowerCase("fr");

  const primary = done
    ? { label: continueLabel, onClick: onContinue }
    : { label: retryLabel, onClick: onRetry };
  const secondary = done
    ? { label: retryLabel, onClick: onRetry }
    : { label: continueLabel, onClick: onContinue };

  return (
    <div
      style={{
        color: "var(--color-text-primary)",
        ...(divided
          ? {
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid var(--color-border-subtle)",
            }
          : { marginTop: 4 }),
      }}
    >
      {/*
        The count reads above the headline rather than under it. It is the
        context the sentence is read in — which pass this was — and under the
        sentence it sat forty pixels from the phrase's own frieze at the foot
        of the panel: two progress objects counting two different things in
        the same corner of the eye.
      */}
      <div className="flex items-center" style={{ gap: 10, marginBottom: 12 }}>
        <PassCounter passes={passes} color={handColor} />
      </div>

      {/*
        « 13 groupes d'affilée, sans t'arrêter » counted a unit the player
        has no unit for. A group is an instant of the run — a note, or the
        keys of one attack — and nobody was counting them while playing; the
        number landed as a score, on a screen whose whole point is that there
        isn't one. What happened is that the passage came out. That is the
        headline, and the reason to play it again is the line under it.
      */}
      <div
        style={{
          fontSize: 21,
          fontWeight: 600,
          lineHeight: 1.25,
          letterSpacing: "-0.01em",
          textWrap: "balance",
        }}
      >
        {title ?? "Passage réussi."}
      </div>

      {line !== null && (
        <div
          style={{
            marginTop: 7,
            fontSize: 14,
            lineHeight: 1.5,
            maxWidth: "46ch",
            color: "var(--color-text-secondary)",
            textWrap: "pretty",
          }}
        >
          {line}
        </div>
      )}

      {/*
        Two acts, one filled and one outlined, at the same height. They used
        to be a button and a bare label, which said one of them was a control
        and the other a caption — of the two, the caption was « Continuer ».
      */}
      <div className="flex items-center" style={{ marginTop: 20, gap: 12 }}>
        <button
          ref={ref}
          type="button"
          onClick={primary.onClick}
          className="inline-flex items-center justify-center transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
          style={{
            minHeight: ACT_H_PX,
            padding: "0 26px",
            borderRadius: 10,
            border: "none",
            background: primaryColor,
            color: "#0d0d12",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {primary.label}
        </button>

        {/*
          `TRACE_EDGE` and not `--color-glass-separator`: 0.10 falls between
          `TRACE_OFF` (1.08:1, carries nothing) and `TRACE_EDGE` (3.06:1) on
          this ground — see the table in `panel-metrics` — so a button drawn
          with it has a boundary nobody can see. 3:1 is what WCAG 1.4.11 asks
          of a meaningful graphic, and the edge of the only other act on the
          screen is one.
        */}
        <button
          type="button"
          onClick={secondary.onClick}
          className="inline-flex items-center justify-center transition-colors hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
          style={{
            minHeight: ACT_H_PX,
            padding: "0 22px",
            borderRadius: 10,
            border: `1px solid ${TRACE_EDGE}`,
            background: "transparent",
            color: "var(--color-text-primary)",
            fontSize: 15,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          {secondary.label}
        </button>
      </div>

      {/*
        Below a rule, on its own row. Hearing the fragment is not a way out of
        this screen: lined up with the two acts, at the same size and colour
        as one of them, it was a third exit from a screen that has two.
      */}
      {audition !== undefined && (
        <div
          className="flex"
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid var(--color-border-subtle)",
          }}
        >
          {audition}
        </div>
      )}

      {handsOn && (
        <HandsOnHint
          items={[
            {
              gesture: "Rejoue",
              effect: "encore une fois",
            },
            { gesture: "Pédale", effect: continueHint },
          ]}
        />
      )}
    </div>
  );
}

function PassCounter({ passes, color }: { passes: number; color: string }) {
  if (passes > PASS_DOTS) {
    return (
      <span
        className="font-mono tabular-nums"
        style={{ fontSize: 11, color: "var(--color-text-secondary)" }}
      >
        {passes} passages
      </span>
    );
  }

  return (
    <>
      <span
        className="font-mono"
        style={{
          fontSize: 11,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--color-text-secondary)",
        }}
      >
        Passages
      </span>

      {/*
        Their own row, so the gap between two dots stays 6 whatever the gap
        between the caption and the group is.
      */}
      <span className="flex items-center" style={{ gap: 6 }}>
        {Array.from({ length: PASS_DOTS }, (_, i) => (
          <span
            key={i}
            aria-hidden
            style={{
              width: 9,
              height: 9,
              borderRadius: 999,
              background: i < passes ? color : "rgba(255, 255, 255, 0.14)",
              boxShadow: i < passes ? `0 0 6px ${color}` : "none",
            }}
          />
        ))}
      </span>
    </>
  );
}


/**
 * "Hear what you are building."
 *
 * The three no-rhythm steps are the only ones where the player actually
 * learns which notes to play, and they were the only ones with no way to hear
 * the fragment: S4 and S5 play the piece. Someone could finish a run having
 * never heard what they had just assembled, in a product whose first
 * principle is sound first.
 *
 * `what` names it, and naming it is what keeps the offer worth taking. S0
 * plays the unit whole, both hands, **on its own**, on arrival. « Écouter ce
 * passage » two screens later played back the identical thing, so the button
 * read as a repeat of something the player had not asked to hear twice. On a
 * single-hand step the model is that hand alone — the thing about to be
 * played, and the one thing the journey never sounds anywhere else.
 *
 * Offered before the first pass and again at the end of a clean one — the two
 * moments where a model is informative — and never on autoplay: a sound
 * starting by itself on the twelfth unit is an interruption, not a model.
 */
export function AuditionButton({
  playing,
  onToggle,
  what = "ce passage",
}: {
  readonly playing: boolean;
  readonly onToggle: () => void;
  /** What is sounded: `ce passage`, `la main gauche`. */
  readonly what?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      // One look wherever it sits, under the rule of the end-of-run card: a
      // ghost, but a reachable one. Before the first pass it used to be a
      // bordered strip across the panel, so the same offer read as two
      // different controls within one step.
      className="inline-flex items-center transition-colors hover:bg-white/[0.05] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
      style={{
        padding: "0 14px 0 12px",
        minHeight: ASIDE_H_PX,
        borderRadius: 8,
        border: "none",
        background: "transparent",
        color: "var(--color-text-secondary)",
        fontSize: 13,
        cursor: "pointer",
      }}
    >
      {/*
        « Arrêter » and not « Je m'arrête », which is what the run bands say.
        Not an oversight: this button stops the **recording** playing the
        passage back, and the player's hands are off the keys. « Je m'arrête »
        is the player withdrawing from something they were doing.
      */}
      {playing ? "■ Arrêter" : `▶ Écouter ${what}`}
    </button>
  );
}
